import * as React from 'react';
import { Row, Col } from 'antd';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';
import * as moment from "moment";
import autobind from 'autobind-decorator';
import * as uuid from "uuid";

import { CardController } from 'src/app/shared/card.controller';
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';
import { CalculatePriceService } from 'src/app/bookings/shared/calculate-prices.service';
import { TourItineraryDateMarketConfig } from 'src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-market-config';
import { TourItineraryDateProductsConfig } from 'src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-products-config';
import { TourItineraryDatePriceSummary } from 'src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-price-summary';

interface Props extends ComponentViewProps<TourItineraryDateItem> {
	mode: ControlMode;
	languageCode: string;
	getAgeBandTag: (item: AgeBandItem) => string;
	isDisabled: boolean;
	products: TourItineraryProductItem[];
	itinerary: TourItineraryItem;
}

interface State {
	selectedMarket?: MarketSummary;
}

@observer
export class TourItineraryDatePriceTab extends React.Component<Props, State> {
	@resolve
	private toast: ToastNotifications;

	@resolve
	private calculatePriceService: CalculatePriceService;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			selectedMarket: null
		};
	}

	private getOccupancyColumns(): OccupancyColumn[] {
		const { model, getAgeBandTag } = this.props;
		const ageBands: AgeBandItem[] = model.dynamicProperties.ageBands;
		const paxConfig: ItineraryDatePaxConfig = model.dynamicProperties.paxConfig;

		return paxConfig.occupancies
			.map((m) => {
				const labelChunks: string[] = [];
				const paxes: PersonOccupancy[] = [];
				const quantityByType = [0, 0, 0];
				for (let ageBand of ageBands) {
					const tag = getAgeBandTag(ageBand);
					const quantity = parseInt(m[tag]);
					if (isNaN(quantity) || quantity === 0) {
						continue;
					}
					quantityByType[ageBand.type] += quantity;

					const paxesForAgeBand: PersonOccupancy[] = Array.from(new Array(quantity))
						.map((_, i) => ({
							id: uuid.v4(),
							name: `${tag}${i + 1}`,
							age: ageBand.minAge
						}));
					labelChunks.push(`${quantity}${tag}`);
					paxes.push(...paxesForAgeBand);
				}

				return {
					name: labelChunks.join('+'),
					adults: quantityByType[0],
					children: quantityByType[1],
					infants: quantityByType[2],
					occupancy: {
						unit: 1,
						paxs: paxes
					}
				};
			});
	}

	private getProductsForMarket(selectedMarket?: MarketSummary): ItineraryDateProductPriceConfig[] {
		if (selectedMarket == null) {
			return [];
		}

		const { model, languageCode, products } = this.props;
		const itineraryProducts = products || [];
		const marketPrice = (model.dynamicProperties.marketsPrice as ItineraryDateMarketPrice[]).find(m => m.marketId === selectedMarket.id);

		const startDate = moment.utc(itineraryProducts[0].dateFrom);
		return itineraryProducts
			.map((m) => {
				let product = marketPrice.products.find(p => p.tourItineraryProductId === m.id);
				if (product == null) {
					product = {
						tourItineraryProductId: m.id,
						occupanciesPrice: []
					};
					marketPrice.products.push(product);
				}

				const dateFrom = moment.utc(model.dateFrom);
				const productDateFrom = moment.utc(m.dateFrom);
				const productDateTo = moment.utc(m.dateTo)
				const productDuration = productDateTo.diff(productDateFrom, "millisecond");
				const durationToStart = productDateFrom.diff(startDate, "milliseconds");
				const productStartDate = dateFrom.add(startDate.hour(), "hour").add(durationToStart, "milliseconds");
				const productEndDate = productStartDate.clone().add(productDuration, "milliseconds");

				return {
					displayName: m.dynamicProperties.displayName[languageCode] || '',
					serviceTypeDisplayName: m.productServiceTypeDisplayName,
					dateFrom: productStartDate.toDate(),
					dateTo: productEndDate.toDate(),
					...product
				};
			});
	}

	@autobind
	private handleChangeMarket(newMarket: MarketSummary): void {
		const { model } = this.props;
		const marketsPrice: ItineraryDateMarketPrice[] = model.dynamicProperties.marketsPrice;

		if (marketsPrice.every(m => m.marketId !== newMarket.id)) {
			marketsPrice.push({
				marketId: newMarket.id,
				roundPolicyType: 0,
				roundPolicyValue: 50,
				products: []
			});
		}

		this.setState({
			selectedMarket: newMarket
		});
	}

	@autobind
	private async handleClickCalculatePrices(): Promise<void> {
		const { selectedMarket } = this.state;
		const { model, languageCode, setBusy, products } = this.props;

		setBusy(true);
		try {
			const itineraryProducts = products || [];
			const marketPrice = (model.dynamicProperties.marketsPrice as ItineraryDateMarketPrice[]).find(m => m.marketId === selectedMarket.id);
			const occupanciesColumns = this.getOccupancyColumns();

			const bookingDate = model.dateFrom;
			const promises: Promise<void>[] = [];
			for (let product of itineraryProducts) {
				let productPrices = marketPrice.products.find(m => m.tourItineraryProductId === product.id);
				if (productPrices == null) {
					productPrices = {
						tourItineraryProductId: product.id,
						occupanciesPrice: []
					};
					marketPrice.products.push(productPrices);
				}

				const api: Api = {
					apiName: product.productProviderApiName,
					isActive: true,
					isActiveForApi: true,
					name: product.productProviderName,
				};
				for (let { name, occupancy, adults, children, infants } of occupanciesColumns) {
					const dateFrom = ObjectUtils.getDateFromOffset(bookingDate, product.dateFrom.getTime() - product.createdOn.getTime());
					const dateTo = ObjectUtils.getDateFromOffset(bookingDate, product.dateTo.getTime() - product.createdOn.getTime());
					const search = this.calculatePriceService.createSearchRequest(product.productType, [api], false, {
						dateFrom: dateFrom,
						dateTo: dateTo,
						productId: product.productId,
						productExternalId: null,
						supplierId: product.supplierId,
						productServiceTypeId: product.productServiceTypeId,
						occupancies: [occupancy],
						additionalProperties: null,
					});
					const promise = this.calculatePriceService.searchSingleProduct(search, product.productType, selectedMarket.id, bookingDate, languageCode, null)
						.then((singleProductResult) => {
							let computedCost = 0;
							let computedSell = 0;
							let costCurrency = product.supplierDefaultCurrencyIso3Name;
							let productProviderKey = product.productProviderApiName;
							let supplierId = product.supplierId;
							let exchangeRate = 1;
							if (singleProductResult != null) {
								computedCost = singleProductResult.result.costPrice[1].totalPrice;
								computedSell = singleProductResult.result.sellPrice[1].totalPrice;
								costCurrency = singleProductResult.result.costPrice[1].currencyIsoCode;
								supplierId = singleProductResult.result.supplier.internalId;
								productProviderKey = singleProductResult.result.productProviderApiName;
								exchangeRate = singleProductResult.result.sellPrice[1].exchangeRate;
							}

							const occupancyPrice = productPrices.occupanciesPrice.find(m => m.occupancyName === name);
							if (occupancyPrice != null) {
								occupancyPrice.computedCost = computedCost;
								occupancyPrice.computedSell = computedSell;
								occupancyPrice.costCurrency = costCurrency;
								occupancyPrice.exchangeRate = exchangeRate;
								occupancyPrice.supplierId = supplierId;
								occupancyPrice.productProviderKey = productProviderKey;
							} else {
								productPrices.occupanciesPrice.push({
									computedCost,
									computedSell,
									costCurrency,
									exchangeRate,
									supplierId,
									productProviderKey,
									cost: computedCost,
									sell: computedSell,
									occupancyName: name,
									adults: adults,
									children: children,
									infants: infants
								});
							}
						});
					promises.push(promise);
				}
			}

			await Promise.all(promises);
			this.forceUpdate();
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	render(): React.ReactNode {
		const { mode, isDisabled } = this.props;
		if (isDisabled) {
			return null;
		}

		const { selectedMarket } = this.state;
		const productPriceConfigs = this.getProductsForMarket(selectedMarket);
		const occupancyColumns = this.getOccupancyColumns();
		const selectedMarketId = selectedMarket != null ? selectedMarket.id : null;
		const sellCurrencyIsoCode = selectedMarket != null ? selectedMarket.currencyDisplayName : 'GBP';

		return (
			<React.Fragment>
				<Row gutter={16}>
					<Col xs={12}>
						<CardController
							title="Market"
							icon="shop"
							className="card-borderless"
							mode={mode}>
							<TourItineraryDateMarketConfig
								{...this.props}
								selectedMarketId={selectedMarketId}
								onChangeMarket={this.handleChangeMarket}
								onClickCalculatePrices={this.handleClickCalculatePrices}
							/>
						</CardController>
					</Col>
					<Col xs={12}>
						<CardController
							title="Total Price"
							icon="pound"
							className="card-borderless"
							mode={mode}>
							<TourItineraryDatePriceSummary
								{...this.props}
								sellCurrencyIsoCode={sellCurrencyIsoCode}
								occupancyColumns={occupancyColumns}
								productPriceConfigs={productPriceConfigs}
							/>
						</CardController>
					</Col>
				</Row>
				<Row gutter={16}>
					<Col xs={24}>
						<CardController
							title="Products"
							icon="shopping-cart"
							className="card-borderless"
							mode={mode}>
							<TourItineraryDateProductsConfig
								{...this.props}
								sellCurrencyIsoCode={sellCurrencyIsoCode}
								occupancyColumns={occupancyColumns}
								productPriceConfigs={productPriceConfigs}
							/>
						</CardController>
					</Col>
				</Row>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/tour-itinerary-date/form/parts/tour-itinerary-date-price-tab.tsx